import React, { useEffect, useState } from 'react';
import PageHeader from 'components/project/page_header';
import './styles.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import PropTypes from 'prop-types';
import { Alert, Col, Row } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { connect } from 'react-redux';
import { getDashboardData } from '../../actions/dashboard';
import LoadingSpinner from 'components/loading_spinner';
import ActivityCard from './cards/activity';
import DocumentClicksCard from './cards/document_clicks';
import EmailCard from './cards/email';
import ReferralCard from './cards/referral';
import HighlightCard from './cards/highlight';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

export function DashboardTab({ data, dashboardPath, didFetch, error, getDashboardData, projectName }) {
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const {
    activitiesForProperty,
    documentClicks,
    emailStats,
    leadsCreated,
    topReferralSources,
    totalDaysOnMarket
  } = data || {};

  const downloadPDF = async () => {
    // html2canvas is necessary since jsPDF will not have the correct stying without the dom elements
    // first being cloned into a canvas element
    setDownloadingPDF(true);
    const pdf = new jsPDF('portrait', 'pt', 'a4');
    const onClone = (cloneDocument) => {
      cloneDocument.querySelectorAll('.pdf-hide').forEach(pdfHideElement => pdfHideElement.classList.add('hide'));
      cloneDocument.querySelector('.pdf-container').classList.add('pdf-styling');
      cloneDocument.querySelector('.page-header-title').textContent = `Dashboard for ${projectName}`;
    };
    const data = await html2canvas(document.querySelector('.pdf-container'), { onclone: onClone });
    const img = data.toDataURL('image/png');
    const imgProperties = pdf.getImageProperties(img);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProperties.height * pdfWidth) / imgProperties.width;
    pdf.addImage(img, 'PNG', 0, 0, pdfWidth, pdfHeight);
    pdf.save(`${projectName} - Dashboard.pdf`);
    setDownloadingPDF(false);
  };


  const headerActions = didFetch && !error ? (
    <PageHeader.Actions>
      <button className="btn button--text pdf-hide" disabled={downloadingPDF} type="button" onClick={downloadPDF}>
        <FontAwesomeIcon className="mr-2" icon={faDownload}/>
        {downloadingPDF ? 'Downloading PDF...' : 'Download PDF'}
      </button>
    </PageHeader.Actions>
  ) : null;

  useEffect(() => {
    if (!didFetch) {
      getDashboardData(dashboardPath);
    }
  }, []);

  return (
    <div className="dashboard-container">
      <div className="pdf-container">
        <PageHeader actions={headerActions} title="Dashboard"/>

        {!didFetch && (<LoadingSpinner/>)}

        {didFetch && (!error ? (
          <React.Fragment>
            <Row>
              <Col className="mb-5" md={4}>
                <HighlightCard number={totalDaysOnMarket} numberColor="primary" title="Total Days On Market"/>
                <HighlightCard number={leadsCreated} numberColor="secondary" title="Leads Created"/>
              </Col>
              <Col className="mb-5" md={8}>
                <ReferralCard topReferralSources={topReferralSources}/>
              </Col>
            </Row>
            <Row>
              <Col className="mb-5" md={4}>
                <EmailCard emailStats={emailStats}/>
              </Col>
              <Col className="mb-5" md={4}>
                <ActivityCard activitiesForProperty={activitiesForProperty}/>
              </Col>
              <Col className="mb-5" md={4}>
                <DocumentClicksCard documentClicks={documentClicks}/>
              </Col>
            </Row>
          </React.Fragment>
        ) : (
          <Alert bsStyle="danger">
            <div className="d-flex mb-3 justify-content-center align-items-center">
              <div className="relative d-inline-block">
                <FontAwesomeIcon className="error-icon" icon={faExclamationCircle} size="2xl"/>
                <h3 className="m-0">An Error has occurred</h3>
              </div>
            </div>
            <div className="text-center" style={{ fontSize: '15px' }}>
              <div>We are unable to load your dashboard.</div>
              <div>Our team has been notified and are working to fix this issue</div>
            </div>
          </Alert>
        ))}
      </div>
    </div>
  );
}

DashboardTab.propTypes = {
  dashboardPath: PropTypes.string.isRequired,
  data: PropTypes.shape({
    activitiesForProperty: PropTypes.array,
    documentClicks: PropTypes.object,
    emailStats: PropTypes.object,
    leadsCreated: PropTypes.number,
    topReferralSources: PropTypes.shape({
      labels: PropTypes.arrayOf(PropTypes.string).isRequired,
      datasets: PropTypes.arrayOf(
        PropTypes.shape({
          label: PropTypes.string.isRequired,
          data: PropTypes.array.isRequired,
        })
      ).isRequired,
    }),
    totalDaysOnMarket: PropTypes.number
  }),
  didFetch: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  getDashboardData: PropTypes.func.isRequired,
  projectName: PropTypes.string.isRequired
};

const mapStateToProps = (state) => {
  return {
    data: state.dashboard.data,
    didFetch: state.dashboard.didFetch,
    error: state.dashboard.error,
    projectName: state.project.displayName,
  };
};

const mapDispatchToProps = {
  getDashboardData,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardTab);
